import { useEffect, useRef, useState } from "react";
import { Button, Segmented } from "antd";
import { RedoOutlined, UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import {
  renderSignatureFrame,
  type InkColor,
  type SignatureSource,
} from "@/lib/signature/removeBackground";

type BrushSize = "small" | "medium" | "large";

const BRUSH_RATIO: Record<BrushSize, number> = {
  small: 0.01,
  medium: 0.025,
  large: 0.05,
};

const HISTORY_LIMIT = 20;

const checkerboard: React.CSSProperties = {
  backgroundColor: "#fff",
  backgroundImage:
    "linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
};

const capturePointer = (el: Element, pointerId: number) => {
  try {
    el.setPointerCapture(pointerId);
  } catch {
    return;
  }
};

interface Point {
  x: number;
  y: number;
}

interface SignatureEraserProps {
  source: SignatureSource;
  sensitivity: number;
  ink: InkColor;
  mask: Uint8Array;
  onCommit: () => void;
}

function SignatureEraser({
  source,
  sensitivity,
  ink,
  mask,
  onCommit,
}: SignatureEraserProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokeRef = useRef<{ last: Point | null } | null>(null);
  const [brush, setBrush] = useState<BrushSize>("medium");
  const [history, setHistory] = useState<Uint8Array[]>([]);
  const [version, setVersion] = useState(0);
  const [cursor, setCursor] = useState<{
    x: number;
    y: number;
    d: number;
  } | null>(null);

  const { width: w, height: h } = source;
  const radius = Math.max(2, Math.round(Math.max(w, h) * BRUSH_RATIO[brush]));
  const [hasErased, setHasErased] = useState(() => mask.some((v) => v === 1));

  useEffect(() => {
    setHistory([]);
    setHasErased(mask.some((v) => v === 1));
  }, [mask]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = w;
    canvas.height = h;
    const frame = renderSignatureFrame(source, {
      sensitivity,
      ink,
      erased: mask,
    });
    canvas.getContext("2d")!.putImageData(frame.imageData, 0, 0);
  }, [source, sensitivity, ink, mask, version, w, h]);

  const toImage = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      point: {
        x: ((e.clientX - rect.left) * w) / rect.width,
        y: ((e.clientY - rect.top) * h) / rect.height,
      },
      css: { x: e.clientX - rect.left, y: e.clientY - rect.top },
      scale: rect.width / w,
    };
  };

  const stamp = (ctx: CanvasRenderingContext2D, { x, y }: Point) => {
    const cx = Math.round(x);
    const cy = Math.round(y);
    const r2 = radius * radius;
    for (let dy = -radius; dy <= radius; dy++) {
      const yy = cy + dy;
      if (yy < 0 || yy >= h) continue;
      for (let dx = -radius; dx <= radius; dx++) {
        const xx = cx + dx;
        if (xx < 0 || xx >= w || dx * dx + dy * dy > r2) continue;
        mask[yy * w + xx] = 1;
      }
    }
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  };

  const paintTo = (p: Point) => {
    const canvas = canvasRef.current;
    const stroke = strokeRef.current;
    if (!canvas || !stroke) return;
    const ctx = canvas.getContext("2d")!;
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    const from = stroke.last ?? p;
    const dist = Math.hypot(p.x - from.x, p.y - from.y);
    const steps = Math.max(1, Math.ceil(dist / Math.max(1, radius / 2)));
    for (let i = 1; i <= steps; i++) {
      const k = i / steps;
      stamp(ctx, {
        x: from.x + (p.x - from.x) * k,
        y: from.y + (p.y - from.y) * k,
      });
    }
    ctx.restore();
    stroke.last = p;
  };

  const pushHistory = () =>
    setHistory((prev) => [...prev.slice(-(HISTORY_LIMIT - 1)), mask.slice()]);

  const commit = () => {
    setHasErased(mask.some((v) => v === 1));
    setVersion((v) => v + 1);
    onCommit();
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    capturePointer(e.currentTarget, e.pointerId);
    pushHistory();
    strokeRef.current = { last: null };
    paintTo(toImage(e).point);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const { point, css, scale } = toImage(e);
    setCursor({ x: css.x, y: css.y, d: radius * 2 * scale });
    if (strokeRef.current) paintTo(point);
  };

  const endStroke = () => {
    if (!strokeRef.current) return;
    strokeRef.current = null;
    commit();
  };

  const undo = () => {
    const prev = history[history.length - 1];
    if (!prev) return;
    mask.set(prev);
    setHistory((h) => h.slice(0, -1));
    commit();
  };

  const restoreAll = () => {
    pushHistory();
    mask.fill(0);
    commit();
  };

  return (
    <div data-testid="signature-eraser">
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13 }}>
          {t("employee.signature.brushSize")}
        </span>
        <Segmented
          value={brush}
          onChange={(v) => setBrush(v as BrushSize)}
          options={[
            { value: "small", label: t("employee.signature.brushSmall") },
            { value: "medium", label: t("employee.signature.brushMedium") },
            { value: "large", label: t("employee.signature.brushLarge") },
          ]}
        />
        <div style={{ flex: 1 }} />
        <Button
          icon={<UndoOutlined />}
          disabled={history.length === 0}
          onClick={undo}
        >
          {t("employee.signature.undo")}
        </Button>
        <Button
          icon={<RedoOutlined />}
          disabled={!hasErased}
          onClick={restoreAll}
        >
          {t("employee.signature.restoreAll")}
        </Button>
      </div>
      <div
        style={{
          ...checkerboard,
          border: "1px dashed #d9d9d9",
          borderRadius: 8,
          padding: 8,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ position: "relative", lineHeight: 0 }}>
          <canvas
            ref={canvasRef}
            data-testid="signature-eraser-canvas"
            aria-label={t("employee.signature.eraser")}
            style={{
              maxWidth: "100%",
              maxHeight: 360,
              touchAction: "none",
              cursor: "crosshair",
              outline: "1px solid rgba(0,0,0,0.06)",
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endStroke}
            onPointerCancel={endStroke}
            onPointerLeave={() => setCursor(null)}
          />
          {cursor && (
            <div
              style={{
                position: "absolute",
                left: cursor.x - cursor.d / 2,
                top: cursor.y - cursor.d / 2,
                width: cursor.d,
                height: cursor.d,
                borderRadius: "50%",
                border: "1px solid #1677ff",
                background: "rgba(22,119,255,0.08)",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
      </div>
      <p style={{ fontSize: 12, color: "#999", margin: "8px 0 0" }}>
        {t("employee.signature.eraserHint")}
      </p>
    </div>
  );
}

export default SignatureEraser;
